import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Users, Plus, Trash2, Search, ArrowLeft, Mail, Phone, User, Pencil, X
} from 'lucide-react';
import { adminApi } from '../lib/api';
import { toast } from 'sonner';

const AdminUsers = ({ isNewEmployee = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState(searchParams.get('role') || 'all');

  // "create" | "edit" | null
  const [formMode, setFormMode] = useState(isNewEmployee ? 'create' : null);
  const [editingUser, setEditingUser] = useState(null);

  const [formData, setFormData] = useState({
    name: '', email: '', password: '', phone: ''
  });

  useEffect(() => { fetchUsers(); }, [roleFilter]);

  const fetchUsers = async () => {
    try {
      const params = roleFilter !== 'all' ? { role: roleFilter } : {};
      const response = await adminApi.getUsers(params);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  /* ── open edit form ── */
  const handleEditClick = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',           // leave blank — only sent if filled
      phone: user.phone || ''
    });
    setFormMode('edit');
  };

  /* ── create ── */
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createEmployee(formData);
      toast.success('Employee created successfully');
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create employee');
    }
  };

  /* ── update ── */
  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // strip empty password so backend ignores it
      const payload = { ...formData };
      if (!payload.password) delete payload.password;

      await adminApi.updateEmployee(editingUser.user_id, payload);
      toast.success('Employee updated successfully');
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    }
  };

  /* ── delete ── */
  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await adminApi.deleteUser(userId);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const resetForm = () => {
    setFormMode(null);
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', phone: '' });
    if (isNewEmployee) navigate('/admin/users?role=employee');
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':    return 'bg-purple-100 text-purple-700';
      case 'employee': return 'bg-blue-100 text-blue-700';
      default:         return 'bg-gray-100 text-gray-700';
    }
  };

  /* ══════════════════════════════════════════
     SHARED FORM (create & edit)
  ══════════════════════════════════════════ */
  if (formMode) {
    const isEdit = formMode === 'edit';
    return (
      <div className="min-h-screen bg-[#F5F0E6]">
        <DashboardSidebar />
        <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8">
          <Button variant="ghost" onClick={resetForm} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Users
          </Button>

          <div className="bg-white rounded-xl p-6 border border-[#D1CBBF] max-w-5xl">
            <h1
              className="text-2xl font-bold text-[#0A1E40] mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {isEdit ? `Edit Employee — ${editingUser?.name}` : 'Add New Employee'}
            </h1>

            <form
              onSubmit={isEdit ? handleUpdate : handleCreate}
              className="grid grid-cols-2 sm:grid-cols-2 gap-x-6 gap-y-4"
            >
              {/* Name */}
              <div className="col-span-2 sm:col-span-1">
                <Label>Full Name</Label>
                <div className="relative mt-1.5 max-w-md">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF] pointer-events-none" />
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter employee full name"
                    required
                    className="pl-12"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="col-span-2 sm:col-span-1">
                <Label>Email</Label>
                <div className="relative mt-1.5 max-w-md">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF] pointer-events-none" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter employee email address"
                    required
                    className="pl-12"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="col-span-2 sm:col-span-1 sm:mt-0">
                <Label>{isEdit ? 'New Password (leave blank to keep current)' : 'Password'}</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={isEdit ? 'Leave blank to keep current password' : 'Create a password (min 6 chars)'}
                  required={!isEdit}
                  className="mt-1.5 max-w-md"
                />
              </div>

              {/* Phone */}
              <div className="col-span-2 sm:col-span-1 sm:mt-0">
                <Label>Phone (optional)</Label>
                <div className="relative mt-1.5 max-w-md">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF] pointer-events-none" />
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter phone number"
                    className="pl-12"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="col-span-2 flex gap-3 pt-4">
                <Button type="submit" className="bg-[#FF5A36] hover:bg-[#E0492B]">
                  {isEdit ? 'Save Changes' : 'Create Employee'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </main>
      </div>
    );
  }

  /* ══════════════════════════════════════════
     USER LIST
  ══════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar />

      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="admin-users-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1
              className="text-2xl lg:text-3xl font-bold text-[#0A1E40]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Manage Users
            </h1>
            <p className="text-[#4A5568] mt-1">{users.length} users total</p>
          </div>
          <Button
            onClick={() => setFormMode('create')}
            className="bg-[#FF5A36] hover:bg-[#E0492B]"
            data-testid="add-employee-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Employee
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF] pointer-events-none" />
            <Input
              type="text"
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'applicant', 'employee', 'admin'].map((role) => (
              <Button
                key={role}
                variant={roleFilter === role ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRoleFilter(role)}
                className={`capitalize ${
                  roleFilter === role
                    ? 'bg-[#0A1E40] text-white'
                    : 'border-[#D1CBBF] text-[#4A5568] hover:text-[#0A1E40]'
                }`}
              >
                {role === 'all' ? 'All' : role.charAt(0).toUpperCase() + role.slice(1) + 's'}
              </Button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-[#D1CBBF] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8">
                      <div className="animate-pulse">Loading...</div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-[#4A5568]">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#0A1E40] rounded-full flex items-center justify-center text-white font-semibold">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <span className="font-medium text-[#0A1E40]">{user.name}</span>
                        </div>
                      </td>
                      <td className="text-[#4A5568]">{user.email}</td>
                      <td className="text-[#4A5568]">{user.phone || '-'}</td>
                      <td>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="text-[#4A5568] text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          {/* Edit — only for employees */}
                          {user.role === 'employee' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-[#0A1E40] hover:bg-blue-50"
                              onClick={() => handleEditClick(user)}
                              title="Edit employee"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Delete — not for admins */}
                          {user.role !== 'admin' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(user.user_id)}
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminUsers;